import { useEffect, useState } from 'react';

/**
 * Copies one value. The button says what happened ("Copied" / "Copy it by
 * hand"); a polite live region repeats it for screen readers. Without
 * clipboard access (an insecure origin, a denied permission) the value stays
 * visible next to the button, so it can still be selected and copied.
 */
export function CopyButton({ value, label }: { value: string; label: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');

  useEffect(() => {
    if (state !== 'copied') return;
    const timer = setTimeout(() => setState('idle'), 2500);
    return () => clearTimeout(timer);
  }, [state]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setState('copied');
    } catch {
      setState('failed');
    }
  }

  const text = state === 'copied' ? 'Copied' : state === 'failed' ? 'Copy it by hand' : 'Copy';

  return (
    <>
      <button type="button" className="button button--secondary button--small" onClick={copy} aria-label={`${text}: ${label}`}>
        {text}
      </button>
      <span className="visually-hidden" role="status" aria-live="polite">
        {state === 'copied' ? `${label} copied` : state === 'failed' ? `Could not copy ${label}; select it and copy it by hand` : ''}
      </span>
    </>
  );
}
